import { Radio } from "lucide-react";
import { Eyebrow, Reveal } from "./Reveal";
import { PhotoBox } from "./PhotoSlot";

const METHODS = [
  {
    title: "Radio frequency spectrum analysis",
    text: "Detects and characterises RF activity within instrument range and test conditions, helping separate expected signals from those that need investigation. Supported by a Signal Hound analyser processing up to 1.2 million FFT spectrums per second.",
    imageSrc: "/media/oscor-spectrum-analysis-office.jpg",
    imageAlt: "OSCOR spectrum analyser being operated during a radio frequency sweep of an office",
  },
  {
    title: "Non-linear junction detection",
    text: "Detects responses that may indicate semiconductor components, including some electronics that are switched off. Responses are investigated rather than treated as confirmation.",
    imageSrc: "/media/chair-nljd-inspection.jpg",
    imageAlt: "A long-probe non-linear junction detector being drawn across office seating",
  },
  {
    title: "Thermal imaging",
    text: "Highlights unusual heat patterns that merit closer examination. It is one investigative input, not proof by itself.",
    imageSrc: "/media/thermal-entrance-blurred.jpg",
    imageAlt: "Thermal imager checking a door frame for heat from concealed electronics",
  },
  {
    title: "Telephone and line analysis",
    text: "TALAN 3.0 telephone and line analyser, used to detect and locate tampering on digital, analogue and VoIP telephone systems and on associated cabling.",
    imageSrc: "/media/under-desk-cable-inspection.jpg",
    imageAlt: "Cabling beneath a boardroom table being checked with a handheld analyser",
  },
  {
    title: "Optical and lens detection",
    text: "Pinhole camera detection by reverse lens reflection, which identifies camera optics whether or not the device is powered, supported by fibre optic scopes with 360-degree articulation for cavities and minimal openings, and pole cameras for high or otherwise unreachable positions.",
    imageSrc: "/media/pole-camera-ceiling-light.jpg",
    imageAlt: "A pole camera raised to a boardroom ceiling light fitting during an optical inspection",
    imagePos: "50% 30%",
  },
  {
    title: "GSM and cellular detection",
    text: "GSM bug detection for SIM-based audio and video devices, including those built into ordinary objects such as power adapters and chargers.",
    imageSrc: "/media/rf-detector-cabin-scan.jpg",
    imageAlt: "Handheld radio-frequency detector scanning a cabin wall and ceiling line",
  },
  {
    title: "Physical search",
    text: "Fittings opened, furniture examined, and anything delivered or gifted checked directly. The step no instrument replaces.",
    imageSrc: "/media/13-physical-socket.jpg",
    imageAlt: "Gloved hand inspecting an opened wall socket during a physical search",
  },
];

const TARGETS = [
  "Audio bugs, transmitters and recorders",
  "GSM and 4G devices",
  "Hidden and covert cameras",
  "Wired and wireless microphones",
  "Voice-activated recorders",
  "GPS tracking devices",
  "Keystroke loggers",
  "Telephone line taps",
  "Carrier current and coaxial bugs",
  "Wi-Fi and Bluetooth based devices",
  "SIM-activated devices built into chargers and power adapters",
];

const PRINCIPLE_POINTS = [
  "Methods complement one another and reduce the limitations of any single detector.",
  "Suspected findings are verified before they are described as devices.",
  "Results are reported within the documented conditions and limits of the inspection.",
];

export default function EquipmentMethod() {
  return (
    <section id="method" className="border-t border-hairline py-12 sm:py-16" data-testid="method-section">
      <div className="site-x">
        <Reveal>
          <Eyebrow>Equipment &amp; method</Eyebrow>
          <h2 className="mt-4 max-w-xl font-head font-extrabold tracking-tight h-sec">
            No single instrument finds everything.
          </h2>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-sub">
            Instrument readings are treated as evidence to investigate — not
            automatic proof of a covert device.
          </p>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-10 border-t border-hairline pt-12 lg:grid-cols-2">
          <Reveal>
            <h4 className="font-head text-2xl font-bold tracking-tight" data-testid="principle-heading">
              A sweep is a method, not a gadget.
            </h4>
            <p className="mt-4 max-w-md text-base leading-relaxed text-sub">
              A switched-off device may not transmit. A hard-wired device may
              have no battery. A camera may be optical, networked or entirely
              offline.
            </p>
          </Reveal>
          <ol className="flex flex-col gap-5">
            {PRINCIPLE_POINTS.map((point, i) => (
              <li key={point} className="flex items-start gap-4" data-testid={`principle-point-${i + 1}`}>
                <span className="mt-1 text-xs font-semibold tracking-[0.16em] text-violet">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-base leading-relaxed text-ink">{point}</span>
              </li>
            ))}
          </ol>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {METHODS.map(({ title, text, image, imageSrc, imageAlt, imagePos }, i) => (
            <Reveal key={title} delay={(i % 3) * 0.08} className="h-full">
              <article
                className="flex h-full flex-col border border-hairline bg-surface transition-colors duration-300"
                data-testid={`method-card-${i + 1}`}
              >
                {image && <PhotoBox name={image} ratio="aspect-[16/9]" testid={`method-image-${i + 1}`} />}
                {imageSrc && (
                  <img
                    src={imageSrc}
                    alt={imageAlt}
                    loading="lazy"
                    style={imagePos ? { objectPosition: imagePos } : undefined}
                    className="aspect-[16/9] w-full border-b border-hairline object-cover"
                    data-testid={`method-image-${i + 1}`}
                  />
                )}
                <div className="flex flex-1 flex-col gap-3 p-6">
                  {!image && !imageSrc && (
                    <Radio size={18} strokeWidth={1.5} className="text-ink" />
                  )}
                  <h4 className="font-head text-xl font-bold leading-snug">{title}</h4>
                  <p className="text-base leading-relaxed text-sub">{text}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-14">
            <h4 className="text-xs font-semibold uppercase tracking-[0.16em] text-violet">
              What an inspection looks for
            </h4>
            <ul className="mt-5 flex flex-wrap gap-2" data-testid="method-targets-list">
              {TARGETS.map((t) => (
                <li
                  key={t}
                  className="rounded-full border border-hairline bg-surface px-3.5 py-1.5 text-[0.625rem] uppercase tracking-[0.12em] text-sub"
                >
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
